import React, { useState } from 'react';
import _isEmpty from 'lodash-es/isEmpty';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ArrowPathIcon as Refresh } from '@heroicons/react/24/outline';
import AutoSubmitForm from '@teetsh/app/src/components/AutoSave';
import { Combobox } from '@headlessui/react';
import { Field, Form } from 'react-final-form';
import { search } from './search';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { ExclamationTriangleIcon as ExclamationCircle } from '@heroicons/react/24/outline';
import tw from 'twin.macro';
import { useToggleSearchModal } from './atom';
import { useNavigate } from 'react-router-dom';
import useKeyPress from './useKeyPress';
import CategoryResult from './components/CategoryResult';
import OptionWrapper from './components/OptionWrapper';
import { SearchEntityKey, SearchResult } from './types';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import Button from '@teetsh/app/src/components/Buttons/Button';
import TextWithSchoolOption from './components/TextWithSchoolOption';
import { SeancePreview } from './previews/SeancePreview';
import { ElevePreview } from './previews/ElevePreview';
import { CarnetPreview } from './previews/CarnetPreview';
import { ProgrammationPreview } from './previews/ProgrammationPreview';
import { EDTPreview } from './previews/EDTPreview';
import { FdpPreview } from './previews/FdpPreview';
import { SequencePreview } from './previews/SequencePreview';
import { LivretPreview } from '@teetsh/app/src/containers/Search/previews/LivretPreview';
import { format } from '@teetsh/app/src/utils/time/format';
import { NotePreview } from '@teetsh/app/src/containers/Search/previews/NotePreview';
import { EvaluationPreview } from '@teetsh/app/src/containers/Search/previews/EvaluationPreview';
import { ReunionPreview } from './previews/ReunionPreview';
import { FilterBar } from '@teetsh/app/src/containers/Search/FilterBar';
import { entities } from '@teetsh/app/src/containers/Search/entities';
import { Tag } from '@teetsh/app/src/components/Tag';
import { FilterBadge } from '@teetsh/app/src/containers/Search/FilterBadge';
import { useTranslation } from 'react-i18next';
import { useSequenceCustomFieldDefinitions } from 'queries/sequences/customfields/useSequenceCustomFieldDefinitions';

export function GlobalSearch(props: {
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  const { inputRef } = props;
  const toggleSearchModal = useToggleSearchModal();
  const navigate = useNavigate();
  const { location, locale } = useUser();
  const { t } = useTranslation(['common']);
  const { notifyError } = useNotification();
  const [filter, setFilter] = useState<SearchEntityKey | undefined>();
  const [selectedCategory, setSelectedCategory] = useState<
    string | undefined
  >();
  const [result, setResult] = useState<SearchResult>({ isEmpty: true });
  useKeyPress(['k'], () => toggleSearchModal(true));

  const { data: unfilteredCustomFieldDefinitions = [] } =
    useSequenceCustomFieldDefinitions();

  const sequenceCustomFieldDefinitions =
    unfilteredCustomFieldDefinitions.filter(definition =>
      definition.targets.includes('sequence'),
    );

  const fdpCustomFieldDefinitions = unfilteredCustomFieldDefinitions.filter(
    definition => definition.targets.includes('fdp'),
  );

  return (
    <Form<{ query: string }>
      initialValues={{
        query: '',
      }}
      enableReinitialize={false}
      destroyOnUnregister={false}
      onSubmit={values => {
        if (_isEmpty(values?.query)) {
          setResult({ isEmpty: true });
          return Promise.resolve();
        }
        return search({ query: values.query, filter, tz: location })
          .then(v => {
            if (!v || v?.isEmpty) {
              setResult({ isEmpty: true });
              return;
            }
            setResult(v);
          })
          .catch(() => {
            notifyError({
              title: t('common:search.searchError'),
            });
          });
      }}
    >
      {({ handleSubmit, submitting, values, dirty }) => (
        <form
          onSubmit={handleSubmit}
          tw="flex flex-col flex-grow min-h-0 pb-4 h-full"
        >
          <AutoSubmitForm />
          <Combobox<any, any> value={values.query}>
            {({ activeOption }) => (
              <div tw="flex flex-col gap-3 flex-grow min-h-0">
                <div tw="relative pr-6 pt-1 shrink-0">
                  {submitting ? (
                    <Refresh
                      tw="animate-spin pointer-events-none absolute top-5 left-6 h-6 w-6 text-neutral-intense text-opacity-40"
                      aria-hidden="true"
                    />
                  ) : (
                    <MagnifyingGlassIcon
                      tw="pointer-events-none absolute top-5 left-6 h-6 w-6 text-neutral-intense text-opacity-40"
                      aria-hidden="true"
                    />
                  )}
                  <div tw="flex items-baseline pl-14 pr-4">
                    {filter && (
                      <div>
                        <FilterBadge
                          label={t(`common:search.filters.${filter}`)}
                          onClose={() => setFilter(undefined)}
                        />
                      </div>
                    )}
                    <Field
                      withoutMargin
                      name="query"
                      large
                      render={({ input: { onChange } }) => (
                        <Combobox.Input
                          tw="h-12 w-full border-0 bg-transparent text-neutral-intense placeholder-gray-500 focus:ring-0 pb-0"
                          placeholder={t('common:search.placeholder')}
                          ref={inputRef}
                          autoFocus
                          onChange={event => onChange(event.target.value)}
                        />
                      )}
                    />
                  </div>
                </div>
                <hr tw="border-t" />
                {!_isEmpty(values.query) && !filter && dirty && (
                  <div tw="border-b border-neutral-quiet py-2 px-2 shrink-0">
                    <FilterBar
                      result={result}
                      value={selectedCategory}
                      onChange={setSelectedCategory}
                    />
                  </div>
                )}
                {_isEmpty(values.query) && !filter && (
                  <div tw="min-h-0 px-6 pt-3 overflow-auto">
                    <div tw="text-muted text-sm font-medium mb-4">
                      {t('common:search.searchIn')}
                    </div>
                    <div tw="flex gap-2 flex-wrap">
                      {entities.map(entity => (
                        <Tag
                          key={entity}
                          outlined
                          color="gris-supershy"
                          onClick={() => {
                            setFilter(entity);
                            inputRef?.current?.focus();
                          }}
                        >
                          {t(`common:search.filters.${entity}`)}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                {!_isEmpty(values.query) &&
                  result.isEmpty &&
                  dirty &&
                  !submitting && (
                    <div tw="py-14 px-6 text-center text-sm sm:px-14">
                      <ExclamationCircle tw="mx-auto h-6 w-6 text-neutral-quiet" />
                      <p tw="mt-4 font-semibold text-neutral-intense">
                        {t('common:search.noResult')}
                      </p>
                      <p tw="mt-2 text-neutral-quiet">
                        {t('common:search.noResultDescription')}
                      </p>
                    </div>
                  )}
                {!result.isEmpty && (
                  <Combobox.Options
                    static
                    hold
                    tw="flex-grow min-h-0 flex divide-x divide-gray-100"
                  >
                    <div
                      css={[
                        tw`min-w-0 flex-auto px-6 py-4 space-y-4 overflow-y-auto`,
                      ]}
                    >
                      {(result?.seances || []).length > 0 &&
                        (selectedCategory === 'seances' ||
                          !selectedCategory) && (
                          <CategoryResult
                            category={t('common:search.results.seances')}
                          >
                            {(result?.seances || []).map(seance => (
                              <Combobox.Option key={seance.id} value={seance}>
                                {({ active }) => (
                                  <OptionWrapper active={active}>
                                    <TextWithSchoolOption
                                      schoolName={seance.schoolName}
                                      schoolyear={seance.schoolyear}
                                      title={
                                        _isEmpty(seance.title)
                                          ? t(
                                              'common:search.seanceWithEmptyTitle',
                                            )
                                          : seance.title
                                      }
                                    >
                                      {format(
                                        seance.start,
                                        locale,
                                        'eee dd/MM/yy',
                                      )}
                                    </TextWithSchoolOption>
                                  </OptionWrapper>
                                )}
                              </Combobox.Option>
                            ))}
                          </CategoryResult>
                        )}

                      {(result?.eleves || []).length > 0 &&
                        (selectedCategory === 'eleves' ||
                          !selectedCategory) && (
                          <CategoryResult
                            category={t('common:search.results.eleves')}
                          >
                            {(result?.eleves || []).map(item => (
                              <Combobox.Option key={item.id} value={item}>
                                {({ active }) => (
                                  <OptionWrapper active={active}>
                                    <TextWithSchoolOption
                                      schoolName={item.schoolName}
                                      schoolyear={item.schoolyear}
                                      title={`${item.familyName} ${item.givenName}`}
                                    />
                                  </OptionWrapper>
                                )}
                              </Combobox.Option>
                            ))}
                          </CategoryResult>
                        )}
                      {(result?.carnets || []).length > 0 &&
                        (selectedCategory === 'carnets' ||
                          !selectedCategory) && (
                          <CategoryResult
                            category={t('common:search.results.carnets')}
                          >
                            {(result?.carnets || []).map(item => (
                              <Combobox.Option key={item.id} value={item}>
                                {({ active }) => (
                                  <OptionWrapper active={active}>
                                    <TextWithSchoolOption
                                      schoolName={item.schoolName}
                                      schoolyear={item.schoolyear}
                                      title={item.name}
                                    >
                                      {t('common:search.results.xEvaluations', {
                                        count: item.nbOfEvaluations,
                                      })}
                                    </TextWithSchoolOption>
                                  </OptionWrapper>
                                )}
                              </Combobox.Option>
                            ))}
                          </CategoryResult>
                        )}
                      {(result?.programmations || []).length > 0 &&
                        (selectedCategory === 'programmations' ||
                          !selectedCategory) && (
                          <CategoryResult
                            category={t('common:search.results.programmations')}
                          >
                            {(result?.programmations || []).map(item => (
                              <Combobox.Option key={item.id} value={item}>
                                {({ active }) => (
                                  <OptionWrapper active={active}>
                                    <TextWithSchoolOption
                                      schoolName={item.schoolName}
                                      schoolyear={item.schoolyear}
                                      title={item.name}
                                    >
                                      {t('common:search.results.xElements', {
                                        count: item.nbOfItems,
                                      })}
                                    </TextWithSchoolOption>
                                  </OptionWrapper>
                                )}
                              </Combobox.Option>
                            ))}
                          </CategoryResult>
                        )}
                      {(result?.fichesDePrep || []).length > 0 &&
                        (selectedCategory === 'fichesDePrep' ||
                          !selectedCategory) && (
                          <CategoryResult
                            category={t('common:search.results.fichesDePrep')}
                          >
                            {(result?.fichesDePrep || []).map(item => (
                              <Combobox.Option key={item.id} value={item}>
                                {({ active }) => (
                                  <OptionWrapper active={active}>
                                    <TextWithSchoolOption title={item.title} />
                                  </OptionWrapper>
                                )}
                              </Combobox.Option>
                            ))}
                          </CategoryResult>
                        )}
                      {(result?.sequences || []).length > 0 &&
                        (selectedCategory === 'sequences' ||
                          !selectedCategory) && (
                          <CategoryResult
                            category={t('common:search.results.sequences')}
                          >
                            {(result?.sequences || []).map(item => (
                              <Combobox.Option key={item.id} value={item}>
                                {({ active }) => (
                                  <OptionWrapper active={active}>
                                    <TextWithSchoolOption title={item.title} />
                                  </OptionWrapper>
                                )}
                              </Combobox.Option>
                            ))}
                          </CategoryResult>
                        )}
                      {(result?.emploisDuTemps || []).length > 0 &&
                        (selectedCategory === 'emploisDuTemps' ||
                          !selectedCategory) && (
                          <CategoryResult
                            category={t('common:search.results.emploisDuTemps')}
                          >
                            {(result?.emploisDuTemps || []).map(item => (
                              <Combobox.Option key={item.id} value={item}>
                                {({ active }) => (
                                  <OptionWrapper active={active}>
                                    <TextWithSchoolOption
                                      schoolName={item.schoolName}
                                      schoolyear={item.schoolyear}
                                      title={item.name}
                                    >
                                      {t('common:search.results.xSeances', {
                                        count: item.nbOfSeances,
                                      })}
                                    </TextWithSchoolOption>
                                  </OptionWrapper>
                                )}
                              </Combobox.Option>
                            ))}
                          </CategoryResult>
                        )}
                      {(result?.livrets || []).length > 0 &&
                        (selectedCategory === 'livrets' ||
                          !selectedCategory) && (
                          <CategoryResult
                            category={t('common:search.results.livrets')}
                          >
                            {(result?.livrets || []).map(item => (
                              <Combobox.Option key={item.id} value={item}>
                                {({ active }) => (
                                  <OptionWrapper active={active}>
                                    <TextWithSchoolOption
                                      schoolName={item.schoolName}
                                      schoolyear={item.schoolyear}
                                      title={item.name}
                                    />
                                  </OptionWrapper>
                                )}
                              </Combobox.Option>
                            ))}
                          </CategoryResult>
                        )}
                      {(result?.evaluations || []).length > 0 &&
                        (selectedCategory === 'evaluations' ||
                          !selectedCategory) && (
                          <CategoryResult
                            category={t('common:search.results.evaluations')}
                          >
                            {(result?.evaluations || []).map(item => (
                              <Combobox.Option key={item.id} value={item}>
                                {({ active }) => (
                                  <OptionWrapper active={active}>
                                    <TextWithSchoolOption
                                      schoolName={item.schoolName}
                                      schoolyear={item.schoolyear}
                                      title={item.name}
                                    />
                                  </OptionWrapper>
                                )}
                              </Combobox.Option>
                            ))}
                          </CategoryResult>
                        )}

                      {(result?.notes || []).length > 0 &&
                        (selectedCategory === 'notes' || !selectedCategory) && (
                          <CategoryResult
                            category={t('common:search.results.notes')}
                          >
                            {(result?.notes || []).map(item => (
                              <Combobox.Option key={item.id} value={item}>
                                {({ active }) => (
                                  <OptionWrapper active={active}>
                                    <TextWithSchoolOption
                                      schoolName={item.schoolName}
                                      schoolyear={item.schoolyear}
                                      title={item.title}
                                    />
                                  </OptionWrapper>
                                )}
                              </Combobox.Option>
                            ))}
                          </CategoryResult>
                        )}

                      {(result?.reunions || []).length > 0 &&
                        (selectedCategory === 'reunions' ||
                          !selectedCategory) && (
                          <CategoryResult
                            category={t('common:search.results.reunions')}
                          >
                            {(result?.reunions || []).map(item => (
                              <Combobox.Option key={item.id} value={item}>
                                {({ active }) => (
                                  <OptionWrapper active={active}>
                                    <TextWithSchoolOption
                                      schoolName={item.schoolName}
                                      schoolyear={item.schoolyear}
                                      title={item.name}
                                    >
                                      {format(
                                        item.date,
                                        locale,
                                        'eee dd/MM/yy',
                                      )}
                                    </TextWithSchoolOption>
                                  </OptionWrapper>
                                )}
                              </Combobox.Option>
                            ))}
                          </CategoryResult>
                        )}
                    </div>
                    {activeOption && (
                      <div
                        css={[
                          tw`hidden w-1/2 flex-none flex-col overflow-y-auto sm:flex pb-8`,
                        ]}
                      >
                        <div tw="absolute bottom-4 right-4">
                          <Button
                            onClick={() => {
                              navigate(activeOption?.url);
                              toggleSearchModal(false);
                            }}
                            intent="primary"
                            large
                          >
                            {t('common:buttons.open')}
                          </Button>
                        </div>
                        {activeOption.searchEntityType === 'cjSeance' && (
                          <SeancePreview seance={activeOption as any} />
                        )}
                        {activeOption.searchEntityType === 'fdp' && (
                          <FdpPreview
                            fdp={activeOption as any}
                            customFieldDefinitions={fdpCustomFieldDefinitions}
                          />
                        )}
                        {activeOption.searchEntityType === 'eleve' && (
                          <ElevePreview eleve={activeOption as any} />
                        )}
                        {activeOption.searchEntityType === 'carnet' && (
                          <CarnetPreview carnet={activeOption as any} />
                        )}
                        {activeOption.searchEntityType === 'edt' && (
                          <EDTPreview edt={activeOption as any} />
                        )}
                        {activeOption.searchEntityType === 'programmation' && (
                          <>
                            <ProgrammationPreview
                              programmation={activeOption as any}
                            />
                          </>
                        )}
                        {activeOption.searchEntityType === 'sequence' && (
                          <SequencePreview
                            sequence={activeOption as any}
                            customFieldDefinitions={
                              sequenceCustomFieldDefinitions
                            }
                          />
                        )}
                        {activeOption.searchEntityType === 'livret' && (
                          <LivretPreview livret={activeOption} />
                        )}
                        {activeOption.searchEntityType === 'evaluation' && (
                          <EvaluationPreview evaluation={activeOption as any} />
                        )}
                        {activeOption.searchEntityType === 'reunion' && (
                          <ReunionPreview reunion={activeOption as any} />
                        )}
                        {activeOption.searchEntityType === 'note' && (
                          <NotePreview note={activeOption} />
                        )}
                      </div>
                    )}
                  </Combobox.Options>
                )}
              </div>
            )}
          </Combobox>
        </form>
      )}
    </Form>
  );
}
